import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '@mui/material/styles';

// Session timeout configuration
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const WARNING_TIME = 10 * 1000; // 10 seconds before timeout

/**
 * Format remaining time in MM:SS format
 */
const formatTime = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * SessionCounter Component
 * 
 * Displays a persistent session timeout counter in the header
 */
const SessionCounter = () => {
  const { isAuthenticated, logout } = useAuth();
  const theme = useTheme();
  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT);
  const [sessionStart, setSessionStart] = useState(null);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset the session timer
  const resetTimer = React.useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isAuthenticated) {
      setRemainingTime(SESSION_TIMEOUT);
      setSessionStart(null);
      return;
    }

    // Reset session start time
    const now = Date.now();
    setSessionStart(now);
    lastActivityRef.current = now;
    setRemainingTime(SESSION_TIMEOUT);

    // Update countdown every second
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - now;
      const remaining = SESSION_TIMEOUT - elapsed;
      
      if (remaining <= 0) {
        setRemainingTime(0);
        clearInterval(intervalRef.current);
      } else {
        setRemainingTime(remaining);
      }
    }, 1000);

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      // Logout user
      logout();
    }, SESSION_TIMEOUT);
  }, [isAuthenticated]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) {
      resetTimer();
      return;
    }

    // Initialize timer
    resetTimer();

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Add event listeners
    const handleActivity = () => {
      const now = Date.now();
      // Only reset if there was actual activity (not just rapid events)
      if (now - lastActivityRef.current > 1000) {
        resetTimer();
      }
      lastActivityRef.current = now;
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, resetTimer, logout]);

  // Don't show if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const isWarning = remainingTime <= WARNING_TIME;
  const formattedTime = formatTime(remainingTime);

  return (
    <Chip
      icon={<AccessTime sx={{ fontSize: 16 }} />}
      label={
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {formattedTime}
        </Typography>
      }
      size="small"
      sx={{
        height: 32,
        bgcolor: isWarning
          ? theme.palette.warning.main
          : theme.palette.mode === 'dark'
          ? theme.palette.action.hover
          : '#f5f5f7',
        color: isWarning
          ? theme.palette.warning.contrastText
          : theme.palette.text.secondary,
        '& .MuiChip-icon': {
          color: isWarning
            ? theme.palette.warning.contrastText
            : theme.palette.text.secondary,
        },
        transition: 'all 0.3s ease',
        border: isWarning ? `1px solid ${theme.palette.warning.dark}` : 'none',
      }}
    />
  );
};

export default SessionCounter;
